import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent
} from '@mui/material';
import { CheckCircle, PersonAdd } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdminEmployeeForm = ({ userId, userRole, companyId }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    candidatename: '',
    personalemail: '',
    companyemail: '',
    employmentStatus: '',
    department: '',
    designation: '',
    joiningDate: '',
    roleType: '',
    reportingManager: '',
    teamSize: '',
    shiftType: '',
    loginTime: '',
    logoutTime: '',
    selectedResponsibilities: [],
    additionalResponsibilities: ''
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [customDepartment, setCustomDepartment] = useState('');
  const [customRoleType, setCustomRoleType] = useState('');
  
  const [companyInfo, setCompanyInfo] = useState({ emailGenMode: 'automated', emailDomain: '', registration_no: null });
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [empIdError, setEmpIdError] = useState('');
  const [personalEmailError, setPersonalEmailError] = useState('');
  const [seatStatus, setSeatStatus] = useState(null);
  const [loadingSeatStatus, setLoadingSeatStatus] = useState(true);

  const fetchSeatStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_URL}/api/admin/employees/seat-limit?id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setSeatStatus(data.data);
      }
    } catch (err) {
    } finally {
      setLoadingSeatStatus(false);
    }
  };

  React.useEffect(() => {
    fetchSeatStatus();
  }, [userId, companyId]);

  React.useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        let cId = companyId;
        if (!cId) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const uId = payload.id;
          const userRes = await fetch(`${API_URL}/api/cmp/users/${uId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const userData = await userRes.json();
          cId = userData.data?.company_id;
        }
        if (cId) {
          const cmpRes = await fetch(`${API_URL}/api/cmp/${cId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const cmpData = await cmpRes.json();
          if (cmpData.data) {
            setCompanyInfo({
              emailGenMode: cmpData.data.emailGenMode || 'automated',
              emailDomain: cmpData.data.emailDomain || '',
              registration_no: cmpData.data.registration_no
            });
          }
        }
      } catch (err) {
      }
    };
    fetchCompanyData();
  }, [companyId]);

  React.useEffect(() => {
    if (companyInfo.emailGenMode === 'automated' && companyInfo.emailDomain && formData.candidatename.trim()) {
      const domain = companyInfo.emailDomain.toLowerCase();
      const parts = formData.candidatename.trim().split(/\s+/).filter(Boolean);
      let suggestions = [];
      if (parts.length === 1) {
        const f = parts[0].toLowerCase();
        suggestions.push(`${f}@${domain}`);
      } else if (parts.length >= 2) {
        const f = parts[0].toLowerCase();
        const l = parts[parts.length - 1].toLowerCase();
        suggestions.push(`${f}.${l}@${domain}`);
        suggestions.push(`${f}${l}@${domain}`);
      }
      setEmailSuggestions(suggestions);
    } else {
      setEmailSuggestions([]);
    }
  }, [formData.candidatename, companyInfo]);

  const validateCandidateName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setNameError('Candidate Full Name is required.');
      return false;
    }
    const nameRegex = /^[a-zA-Z\s.'-]+$/;
    if (!nameRegex.test(trimmed)) {
      setNameError('Candidate Name should contain only letters and spaces (no numbers or special characters).');
      return false;
    }
    setNameError('');
    return true;
  };

  const checkEmpIdUniqueness = async (idToCheck) => {
    const trimmed = (idToCheck || '').trim();
    if (!trimmed) {
      setEmpIdError('');
      return true;
    }

    const idRegex = /^[A-Za-z0-9_-]{3,20}$/;
    if (!idRegex.test(trimmed)) {
      setEmpIdError('Employee ID must be 3-20 alphanumeric characters (hyphens and underscores allowed).');
      return false;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no || companyId;
      const res = await fetch(`${API_URL}/api/admin/employees/check-employee-id?employee_id=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.exists) {
        setEmpIdError('Employee ID is already in use in this company.');
        return false;
      } else {
        setEmpIdError('');
        return true;
      }
    } catch {
      return true;
    }
  };

  const checkPersonalEmailUniqueness = async (emailToCheck, compEmail) => {
    const trimmed = (emailToCheck || '').trim().toLowerCase();
    if (!trimmed) {
      setPersonalEmailError('Personal email is required.');
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      setPersonalEmailError('Please enter a valid email address (e.g., user@example.com).');
      return false;
    }

    const cmp = (compEmail !== undefined ? compEmail : formData.companyemail || '').trim().toLowerCase();
    if (cmp && trimmed === cmp) {
      setPersonalEmailError('Personal email cannot be identical to company email.');
      return false;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no || companyId;
      const res = await fetch(`${API_URL}/api/admin/employees/check-personal-email?email=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.exists) {
        setPersonalEmailError('This personal email is already registered in this company.');
        return false;
      } else {
        setPersonalEmailError('');
        return true;
      }
    } catch {
      return true;
    }
  };

  const checkEmailUniqueness = async (emailToCheck, persEmail) => {
    const trimmed = (emailToCheck || '').trim().toLowerCase();
    if (!trimmed) {
      if (companyInfo.emailGenMode === 'manual') {
        setEmailError('Company email is required.');
        return false;
      }
      setEmailError('');
      return true;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      setEmailError('Invalid company email format.');
      return false;
    }

    const domain = (companyInfo.emailDomain || '').toLowerCase();
    if (domain && !trimmed.endsWith(`@${domain}`)) {
      setEmailError(`Company email must end with the registered domain: @${domain}`);
      return false;
    }

    const pers = (persEmail !== undefined ? persEmail : formData.personalemail || '').trim().toLowerCase();
    if (pers && trimmed === pers) {
      setEmailError('Company email cannot be identical to personal email.');
      return false;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no || companyId;
      const res = await fetch(`${API_URL}/api/admin/employees/check-email?email=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.exists) {
        setEmailError('This company email is already registered for this company.');
        return false;
      } else {
        setEmailError('');
        return true;
      }
    } catch {
      return true;
    }
  };

  const roleTypes = [
    "CEO", "CTO", "Board of directors", "Section manager/Department manger", 
    "Manager", "Project Manager", "Product Manager", "Product Owner", "Finance Manager", "HR", "HR Manager", "Hiring Manager", "Priciple engineer/architect", 
    "Team Lead", "Senior Software Engineer", "Software Engineer", 
    "Junior Software Engineer", "Intern"
  ];

  const employmentStatusOptions = [
    "Active", "Deceased", "Resigned"
  ];

  const departmentOptions = [
    "Engineering", "Human Resources", "Sales", "Marketing", 
    "Finance", "Operations", "Product", "Customer Support"
  ];

  const shiftTypeOptions = [
    "General Shift", "Early Shift", "Evening Shift", "Night Shift", "Hybrid Shift", "Remote Shift"
  ];

  const responsibilities = {
    "ceo": ["Strategic planning", "Business development", "Executive leadership", "Stakeholder relations", "Company vision"],
    "cto": ["Technology strategy", "Technical leadership", "Innovation management", "IT governance", "Technical risk assessment"],
    "board of directors": ["Corporate governance", "Strategic oversight", "CEO supervision", "Financial accountability", "Shareholder representation"],
    "section manager/department manger": ["Departmental leadership", "Budget management", "Cross-team coordination", "Department KPI tracking", "Process improvement"],
    "manager": ["Team management", "Project oversight", "Performance evaluation", "Resource allocation", "Department strategy"],
    "hr manager": ["Employee relations", "HR policy development", "Benefits administration", "Employee engagement", "Talent development"],
    "hiring manager": ["Candidate screening", "Interview coordination", "Recruitment strategy", "Onboarding oversight", "Hiring decisions"],
    "priciple engineer/architect": ["System architecture design", "Technical decision-making", "Engineering standards", "Technical mentorship", "Technology roadmap"],
    "team lead": ["Team coordination", "Technical guidance", "Sprint planning", "Progress reporting", "Code reviews"],
    "senior software engineer": ["Complex feature implementation", "Architecture participation", "Technical mentoring", "Advanced problem solving", "Code quality enforcement"],
    "software engineer": ["Software development", "Problem solving", "Code testing", "Documentation", "Feature implementation"],
    "junior software engineer": ["Basic feature implementation", "Bug fixing", "Learning codebase", "Pair programming", "Testing and QA"],
    "intern": ["Learning", "Assigned tasks", "Progress reporting", "Skill development", "Support activities"]
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResponsibilityChange = (responsibility) => {
    setFormData(prev => {
      const current = prev.selectedResponsibilities;
      if (current.includes(responsibility)) {
        return {
          ...prev,
          selectedResponsibilities: current.filter(r => r !== responsibility)
        };
      } else {
        return {
          ...prev,
          selectedResponsibilities: [...current, responsibility]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isNameValid = validateCandidateName(formData.candidatename);
    const isEmpIdValid = await checkEmpIdUniqueness(formData.employee_id);
    const isPersonalValid = await checkPersonalEmailUniqueness(formData.personalemail, formData.companyemail);
    const isCompValid = await checkEmailUniqueness(formData.companyemail, formData.personalemail);

    if (!isNameValid || !isEmpIdValid || !isPersonalValid || !isCompValid || emailError || empIdError || personalEmailError || nameError) {
      setError("Please fix all highlighted errors in the form before submitting.");
      return;
    }

    if (seatStatus?.isExpired) {
      setError("Subscription/Trial has expired. Please upgrade to add employees.");
      return;
    }

    if (seatStatus?.isLimitReached) {
      setError(`User seat limit reached (${seatStatus?.usedSeats}/${seatStatus?.totalSeats}). Please upgrade your plan to  add more employees.`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    const finalFormData = {
      ...formData,
      department: formData.department === 'Other' ? customDepartment.trim() : formData.department,
      roleType: formData.roleType === 'Other' ? customRoleType.trim() : formData.roleType
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/employees/add?id=${userId}&role=${userRole}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(finalFormData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmittedData({
          employeeId: result.data.employee_id,
          companyEmail: result.data.companyemail,
          name: result.data.name,
          personalEmail: result.data.personalemail
        });
        setSubmitSuccess(true);
        fetchSeatStatus();
      } else {
        setError(result.message || 'Failed to add employee');
        fetchSeatStatus();
      }
    } catch (err) {
      setError('An error occurred while submitting the form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setSubmitSuccess(false);
    setCustomDepartment('');
    setCustomRoleType('');
    setFormData({
      employee_id: '',
      candidatename: '',
      personalemail: '',
      companyemail: '',
      employmentStatus: '',
      department: '',
      designation: '',
      joiningDate: '',
      roleType: '',
      reportingManager: '',
      teamSize: '',
      shiftType: '',
      loginTime: '',
      logoutTime: '',
      selectedResponsibilities: [],
      additionalResponsibilities: ''
    });
    setEmailError('');
    setEmailSuggestions([]);
  };

  const availableResponsibilities = formData.roleType 
    ? responsibilities[formData.roleType.toLowerCase()] || []
    : [];

  return (
  <Container maxWidth={false} sx={{ width: '100%' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        
          <Typography variant="h4" component="h1" fontweight="bold" sx={{ flexGrow: 1 }}>
            Add Employee Details
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {seatStatus && (
          <Box sx={{ mb: 3 }}>
            {seatStatus.isLimitReached || seatStatus.isExpired ? (
              <Alert 
                severity="warning" 
                variant="filled"
                sx={{ 
                  borderRadius: 2,
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ⚠️ {seatStatus.isExpired 
                      ? "Trial Expired: Your organization trial has ended. Please upgrade your plan"
                      : `User Limit Reached: ${seatStatus.usedSeats} of ${seatStatus.totalSeats} seats used. You cannot add more employees under your current plan.`}
                  </Typography>
                  <Chip 
                    label={`${seatStatus.usedSeats} / ${seatStatus.totalSeats} Seats Used`} 
                    color="error" 
                    size="small" 
                    sx={{ fontWeight: 'bold', bgcolor: 'white', color: 'error.main' }}
                  />
                </Box>
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f0f7ff', p: 1.5, borderRadius: 2, border: '1px solid #cce5ff' }}>
                <Typography variant="body2" color="primary" fontWeight="medium">
                  <strong>Plan Seat Usage:</strong> {seatStatus.usedSeats} of {seatStatus.totalSeats} seats used ({seatStatus.remainingSeats} {seatStatus.remainingSeats === 1 ? 'seat' : 'seats'} available)
                </Typography>
                <Chip 
                  label={`${seatStatus.remainingSeats} Left`} 
                  color="primary" 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee ID (Optional)"
                name="employee_id"
                value={formData.employee_id}
                onChange={(e) => {
                  handleInputChange(e);
                  checkEmpIdUniqueness(e.target.value);
                }}
                onBlur={(e) => checkEmpIdUniqueness(e.target.value)}
                error={!!empIdError}
                helperText={empIdError || "Leave empty to auto-generate Employee ID, or type a custom one (3-20 alphanumeric characters)."}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Candidate Full Name"
                name="candidatename"
                value={formData.candidatename}
                onChange={(e) => {
                  handleInputChange(e);
                  validateCandidateName(e.target.value);
                }}
                onBlur={(e) => validateCandidateName(e.target.value)}
                error={!!nameError}
                variant="outlined"
                helperText={nameError || "Enter full name (e.g., John Doe or John Middle Doe)"}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="email"
                label="Personal Email"
                name="personalemail"
                value={formData.personalemail}
                onChange={(e) => {
                  handleInputChange(e);
                  checkPersonalEmailUniqueness(e.target.value, formData.companyemail);
                }}
                onBlur={(e) => checkPersonalEmailUniqueness(e.target.value, formData.companyemail)}
                error={!!personalEmailError}
                helperText={personalEmailError || "Enter employee's personal email address (e.g., name@example.com)."}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required={companyInfo.emailGenMode === 'manual'}
                type="email"
                label="Company Email"
                name="companyemail"
                value={formData.companyemail}
                onChange={(e) => {
                  handleInputChange(e);
                  checkEmailUniqueness(e.target.value, formData.personalemail);
                }}
                onBlur={(e) => checkEmailUniqueness(e.target.value, formData.personalemail)}
                error={!!emailError}
                helperText={
                  emailError || (companyInfo.emailGenMode === 'automated'
                    ? "Suggestions will appear as you type candidate name. You can click a suggestion or edit manually."
                    : "Enter employee's official company email address.")
                }
                variant="outlined"
              />
              {companyInfo.emailGenMode === 'automated' && emailSuggestions.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Suggestions:</Typography>
                  {emailSuggestions.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      clickable
                      color={formData.companyemail === suggestion ? "primary" : "default"}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, companyemail: suggestion }));
                        checkEmailUniqueness(suggestion);
                      }}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Grid>

            {/* Employment Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">
                Employment Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Employment Status</InputLabel>
                <Select
                  name="employmentStatus"
                  value={formData.employmentStatus}
                  onChange={handleInputChange}
                  label="Employment Status"
                >
                  {employmentStatusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  label="Department"
                >
                  {departmentOptions.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                  <MenuItem value="Other">Other (Custom Department)</MenuItem>
                </Select>
              </FormControl>
              {formData.department === 'Other' && (
                <TextField
                  fullWidth
                  required
                  label="Custom Department"
                  value={customDepartment}
                  onChange={(e) => setCustomDepartment(e.target.value)}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Joining Date"
                name="joiningDate"
                value={formData.joiningDate}
                onChange={handleInputChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Shift Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">
                Shift Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Shift Type</InputLabel>
                <Select
                  name="shiftType"
                  value={formData.shiftType}
                  onChange={handleInputChange}
                  label="Shift Type"
                >
                  <MenuItem value="">Select Shift Type</MenuItem>
                  {shiftTypeOptions.map((shift) => (
                    <MenuItem key={shift} value={shift}>
                      {shift}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="time"
                label="Login Time"
                name="loginTime"
                value={formData.loginTime}
                onChange={handleInputChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="time"
                label="Logout Time"
                name="logoutTime"
                value={formData.logoutTime}
                onChange={handleInputChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Role Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">
                Role Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Role Type</InputLabel>
                <Select
                  name="roleType"
                  value={formData.roleType}
                  onChange={handleInputChange}
                  label="Role Type"
                >
                  {roleTypes.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                  <MenuItem value="Other">Other (Custom Role Type)</MenuItem>
                </Select>
              </FormControl>
              {formData.roleType === 'Other' && (
                <TextField
                  fullWidth
                  required
                  label="Custom Role Type"
                  value={customRoleType}
                  onChange={(e) => setCustomRoleType(e.target.value)}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>

       

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Team Size"
                name="teamSize"
                value={formData.teamSize}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* Responsibilities */}
            {availableResponsibilities.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Select Responsibilities
                </Typography>
                <FormGroup row>
                  {availableResponsibilities.map((resp) => (
                    <Grid item xs={12} sm={6} md={4} key={resp}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.selectedResponsibilities.includes(resp)}
                            onChange={() => handleResponsibilityChange(resp)}
                          />
                        }
                        label={resp}
                      />
                    </Grid>
                  ))}
                </FormGroup>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Responsibilities"
                name="additionalResponsibilities"
                value={formData.additionalResponsibilities}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="Enter any additional responsibilities..."
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || seatStatus?.isLimitReached || seatStatus?.isExpired}
                sx={{ mt: 2, py: 1.5, ml: 100 }}
              >
                {isSubmitting 
                  ? 'Submitting...' 
                  : (seatStatus?.isLimitReached 
                      ? 'Seat Limit Reached' 
                      : (seatStatus?.isExpired ? 'Trial Expired' : 'Add Employee'))}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Success Dialog */}
      <Dialog
        open={submitSuccess}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
            <CheckCircle sx={{ mr: 1, fontSize: 32 }} />
            <Typography variant="h5">Employee Added Successfully!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {submittedData && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="h6">
                      {submittedData.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee ID
                    </Typography>
                    <Chip 
                      label={submittedData.employeeId} 
                      color="primary" 
                      sx={{ mt: 0.5, fontWeight: 'bold' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Personal Email
                    </Typography>
                    <Typography variant="body1">
                      {submittedData.personalEmail}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company Email
                    </Typography>
                    <Typography variant="body1">
                      {submittedData.companyEmail}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Add Another Employee
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminEmployeeForm;